import {useEffect, useRef} from 'react';
import lazyLoader from '@ui/utils/loaders/lazy-loader';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';

// CUSTOM(albaniantheatre): Cloudflare Turnstile widget, rendered explicitly so
// the token can be pushed into the react-hook-form state of the login form.
const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: TurnstileRenderOptions,
      ) => string | undefined;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface Props {
  siteKey: string;
  onTokenChange: (token: string) => void;
  // bump to discard a spent token and show a fresh challenge
  resetCount?: number;
  className?: string;
}
export function TurnstileWidget({
  siteKey,
  onTokenChange,
  resetCount = 0,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();
  const isDarkMode = useIsDarkMode();

  // keep the latest callback without re-rendering the widget on every keystroke
  const onTokenChangeRef = useRef(onTokenChange);
  onTokenChangeRef.current = onTokenChange;

  useEffect(() => {
    let cancelled = false;

    lazyLoader.loadAsset(SCRIPT_URL, {id: 'cf-turnstile-js'}).then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: isDarkMode ? 'dark' : 'light',
        callback: token => onTokenChangeRef.current(token),
        'expired-callback': () => onTokenChangeRef.current(''),
        'error-callback': () => onTokenChangeRef.current(''),
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = undefined;
      }
    };
  }, [siteKey, isDarkMode]);

  useEffect(() => {
    if (resetCount && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onTokenChangeRef.current('');
    }
  }, [resetCount]);

  return <div ref={containerRef} className={className} />;
}
